import React, { useState } from 'react';
import { useWebhookConfig } from '../hooks/useWebhookConfig';
import { WebhookSettingsProps } from '../types';

export const WebhookSettings: React.FC<WebhookSettingsProps> = ({
  onSettingsChange,
  className = '',
}) => {
  const { settings, isValid, updateSettings, resetSettings } = useWebhookConfig();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(settings);

  const handleInputChange = (field: keyof typeof settings, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    updateSettings(formData);
    setIsEditing(false);
    onSettingsChange?.(formData);
  };

  const handleCancel = () => {
    setFormData(settings);
    setIsEditing(false);
  };

  const handleReset = () => {
    resetSettings();
    setFormData(settings);
  };

  return (
    <div className={`bg-white shadow-lg rounded-lg p-8 ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          웹훅 설정
        </h2>
        <div className="flex space-x-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              편집
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                저장
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                취소
              </button>
            </>
          )}
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            초기화
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* 웹훅 URL 설정 */}
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              연락처 웹훅 URL
            </label>
            <input
              type="url"
              value={isEditing ? formData.contactWebhookUrl : settings.contactWebhookUrl}
              onChange={(e) => handleInputChange('contactWebhookUrl', e.target.value)}
              disabled={!isEditing}
              placeholder="https://hook.us2.make.com/your-contact-webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              분석 웹훅 URL
            </label>
            <input
              type="url"
              value={isEditing ? formData.analyticsWebhookUrl : settings.analyticsWebhookUrl}
              onChange={(e) => handleInputChange('analyticsWebhookUrl', e.target.value)}
              disabled={!isEditing}
              placeholder="https://hook.us2.make.com/your-analytics-webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              배치 웹훅 URL
            </label>
            <input
              type="url"
              value={isEditing ? formData.batchWebhookUrl : settings.batchWebhookUrl}
              onChange={(e) => handleInputChange('batchWebhookUrl', e.target.value)}
              disabled={!isEditing}
              placeholder="https://hook.us2.make.com/your-batch-webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        {/* 고급 설정 */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">고급 설정</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                재시도 횟수
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={isEditing ? formData.retryAttempts : settings.retryAttempts}
                onChange={(e) => handleInputChange('retryAttempts', parseInt(e.target.value))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                재시도 지연 (ms)
              </label>
              <input
                type="number"
                min="100"
                max="10000"
                step="100"
                value={isEditing ? formData.retryDelay : settings.retryDelay}
                onChange={(e) => handleInputChange('retryDelay', parseInt(e.target.value))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                타임아웃 (ms)
              </label>
              <input
                type="number"
                min="1000"
                max="60000"
                step="1000"
                value={isEditing ? formData.timeout : settings.timeout}
                onChange={(e) => handleInputChange('timeout', parseInt(e.target.value))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>

        {/* 상태 표시 */}
        <div className="border-t pt-6">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isValid ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600">
              {isValid ? '웹훅 설정이 완료되었습니다.' : '웹훅 URL을 설정해주세요.'}
            </span>
          </div>
        </div>

        {/* 도움말 */}
        <div className="bg-blue-50 p-4 rounded-md">
          <h4 className="text-sm font-medium text-blue-900 mb-2">설정 방법</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Make.com에서 웹훅 시나리오를 생성하세요</li>
            <li>• 각 웹훅 URL을 복사하여 해당 필드에 붙여넣으세요</li>
            <li>• 최소 하나의 웹훅 URL이 설정되어야 합니다</li>
            <li>• 설정은 브라우저 로컬 스토리지에 저장됩니다</li>
          </ul>
        </div>
      </div>
    </div>
  );
}; 